import { apiClient } from './client';
import type {
  Location,
  LocationArea,
  LocationAreaWrite,
  LocationDetail,
  LocationRegion,
  LocationRegionWrite,
  LocationState,
  LocationType,
  LocationWrite,
} from '@/types/oscar';

export interface LocationsQuery {
  region?: number;
  area?: number;
  state?: number;
  isBranch?: boolean;
  isStockLocation?: boolean;
  activeOnly?: boolean;
  search?: string;
  /** 'none' = top-level sites only; a number = that site's sub-locations. */
  parent?: number | 'none';
  type?: LocationType;
}

function qs(q: LocationsQuery): string {
  const p = new URLSearchParams();
  if (q.region != null) p.set('region', String(q.region));
  if (q.area != null) p.set('area', String(q.area));
  if (q.state != null) p.set('state', String(q.state));
  if (q.isBranch != null) p.set('isBranch', String(q.isBranch));
  if (q.isStockLocation != null) p.set('isStockLocation', String(q.isStockLocation));
  if (q.activeOnly != null) p.set('activeOnly', String(q.activeOnly));
  if (q.search) p.set('search', q.search);
  if (q.parent != null) p.set('parent', String(q.parent));
  if (q.type) p.set('type', q.type);
  const s = p.toString();
  return s ? `?${s}` : '';
}

export function getLocations(query: LocationsQuery = {}, signal?: AbortSignal): Promise<Location[]> {
  return apiClient.get<Location[]>(`/api/locations${qs(query)}`, signal);
}
export function getLocation(id: number, signal?: AbortSignal): Promise<LocationDetail> {
  return apiClient.get<LocationDetail>(`/api/locations/${id}`, signal);
}
export function createLocation(body: LocationWrite): Promise<Location> {
  return apiClient.post<Location>('/api/locations', body);
}
export function updateLocation(id: number, body: LocationWrite): Promise<Location> {
  return apiClient.patch<Location>(`/api/locations/${id}`, body);
}
export function deleteLocation(id: number): Promise<{ LocationId: number; IsDeleted: boolean }> {
  return apiClient.post(`/api/locations/${id}/delete`);
}

export function getRegions(signal?: AbortSignal): Promise<LocationRegion[]> {
  return apiClient.get<LocationRegion[]>('/api/locations/regions', signal);
}
export function getAreas(region?: number, signal?: AbortSignal): Promise<LocationArea[]> {
  const q = region != null ? `?region=${region}` : '';
  return apiClient.get<LocationArea[]>(`/api/locations/areas${q}`, signal);
}
export function getStates(signal?: AbortSignal): Promise<LocationState[]> {
  return apiClient.get<LocationState[]>('/api/locations/states', signal);
}

// ── Region / Area management (admin) ────────────────────────────────────────

export function createRegion(body: LocationRegionWrite): Promise<LocationRegion> {
  return apiClient.post<LocationRegion>('/api/locations/regions', body);
}
export function updateRegion(id: number, body: LocationRegionWrite): Promise<unknown> {
  return apiClient.patch(`/api/locations/regions/${id}`, body);
}
export function deleteRegion(id: number): Promise<unknown> {
  return apiClient.post(`/api/locations/regions/${id}/delete`);
}

export function createArea(body: LocationAreaWrite): Promise<LocationArea> {
  return apiClient.post<LocationArea>('/api/locations/areas', body);
}
export function updateArea(id: number, body: LocationAreaWrite): Promise<unknown> {
  return apiClient.patch(`/api/locations/areas/${id}`, body);
}
export function deleteArea(id: number): Promise<unknown> {
  return apiClient.post(`/api/locations/areas/${id}/delete`);
}
